
import './index.css'
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ShoppingBag, Clock, ArrowRight, MapPin, CheckCircle2, Plus, Minus, X, ArrowLeft, Trash2 } from 'lucide-react';

export default function App() {
  const [time, setTime] = useState('');
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isContactSubmitted, setIsContactSubmitted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [cartForm, setCartForm] = useState({ name: '', email: '', company: '' });
  const [isCartSubmitting, setIsCartSubmitting] = useState(false);
  const [cartSubmitSuccess, setCartSubmitSuccess] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Listen for browser navigation (forward/back)
  useEffect(() => {
    const onLocationChange = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', onLocationChange);
    return () => window.removeEventListener('popstate', onLocationChange);
  }, []);

  // Custom navigation function to prevent full page reloads in the preview
  const navigateTo = (path, e) => {
    if (e) e.preventDefault();
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    window.scrollTo(0, 0);
  };

  // Simulate a live clock for that "Open 24/7" feel
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute:'2-digit', second:'2-digit' }) + ' LOCAL');
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const calculateItemTotal = (item) => {
    if (item.isCustom) return 0;
    let total = item.basePriceNum;
   
    // Calculate variable additions based on item rules
    if (item.variableConfig && item.variableCount !== undefined) {
      if (item.id === '01') {
        // Base covers 3 pages. $75 per extra page.
        total += Math.max(0, item.variableCount - 3) * 75;
      } else if (item.id === '03') {
        // Base covers 20 slides. $500 per block of 5 extra slides.
        total += Math.ceil(Math.max(0, item.variableCount - 20) / 5) * 500;
      } else if (item.id === '04') {
        // Base covers 5 assets. $125 per extra asset (estimated bulk logic).
        total += Math.max(0, item.variableCount - 5) * 125;
      } else if (item.id === '05') {
        // Additional hours are $500 each.
        total += Math.max(0, item.variableCount) * 500;
      }
    }
    return total * item.quantity;
  };

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
     
      // Set default counts for the variable inputs
      let defaultVar = 0;
      if (item.id === '01') defaultVar = 3;  // 3 pages
      if (item.id === '03') defaultVar = 20; // 20 slides
      if (item.id === '04') defaultVar = 5;  // 5 assets
      if (item.id === '05') defaultVar = 0;  // 0 extra hours
     
      return [...prev, { ...item, quantity: 1, variableCount: defaultVar }];
    });
    setIsCartOpen(true);
  };

  const updateQuantity = (id, delta) => {
    setCart(prev => prev.map(i => {
      if (i.id === id) {
        const newQ = i.quantity + delta;
        return newQ > 0 ? { ...i, quantity: newQ } : i;
      }
      return i;
    }));
  };

  const updateVariableCount = (id, val) => {
    const num = parseInt(val) || 0;
    setCart(prev => prev.map(i => i.id === id ? { ...i, variableCount: num } : i));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const handleCartSubmit = async (e) => {
    e.preventDefault();
    setIsCartSubmitting(true);

    const itemsSummary = cart.map(item =>
      `${item.quantity}x ${item.name} (${item.id})` +
      (item.variableConfig ? ` - ${item.variableConfig.label.split(' ')[0]}: ${item.variableCount}` : '') +
      ` - $${calculateItemTotal(item)}`
    ).join('\n');

    const payload = {
      name: cartForm.name,
      email: cartForm.email,
      company: cartForm.company,
      subject: `New Service Request from ${cartForm.name}`,
      subtotal: `$${cartSubtotal}${hasCustomItems ? '+' : ''}`,
      items: itemsSummary
    };

    try {
      const response = await fetch("https://formspree.io/f/mojprjek", {
        method: "POST",
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setCartSubmitSuccess(true);
        setCart([]);
        setCartForm({ name: '', email: '', company: '' });
        setTermsAccepted(false);
      } else {
        console.error("Cart submission failed");
      }
    } catch (error) {
      console.error("Error submitting cart:", error);
    } finally {
      setIsCartSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
   
    try {
      const response = await fetch("https://formspree.io/f/mojprjek", {
        method: "POST",
        body: data,
        headers: {
          'Accept': 'application/json'
        }
      });
     
      if (response.ok) {
        setIsContactSubmitted(true);
        form.reset();
      } else {
        console.error("Form submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const cartSubtotal = cart.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const hasCustomItems = cart.some(item => item.isCustom);

  const menuItems = [
    {
      id: '01',
      name: 'THE BRIEF CHECKUP',
      desc: 'Rapid brief review. We spot the strategic holes and advise on how to strengthen your brief so you can move forward with confidence.',
      modalDesc: 'Rapid brief review. We spot the strategic holes and advise on how to strengthen your project or creative brief so you can move forward with confidence. An annotated brief document and strategic recommendations will be returned within 24 hours, with an optional 15-min video or phone walkthrough (must be scheduled within 3 days of annotated brief delivery).',
      time: '1 DAY',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['1x Annotated Brief Document', 'Strategic Recommendations List', '15-min video or phone walkthrough (optional)'],
        requirements: 'Brief must be submitted in Google Docs, Word, or Slides format. Access must be given to all hyperlinks in the doc.',
        notes: '24 hour turnaround time begins upon receipt of payment and all materials.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '02',
      name: 'STRATEGY CHAT',
      desc: 'Live 2-hour virtual session to refine your early-stage ideas, brand positioning, or GTM/campaign strategy.',
      modalDesc: 'Live 2-hour virtual session with an experienced strategist to refine your early-stage business or creative ideas, brand positioning, or GTM/campaign strategy. We’ll work with you to process and clarify your thoughts and ideas in a focused fashion, so you leave with ways to progress your strategy forward.\n\nNote: This session may not provide all the answers. If a deeper dive is needed, consider the bespoke options.',
      time: '1 DAY',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['2-Hour Live Working Session (Video or Phone)', 'Optional Call Recording & Transcript', 'Consolidated Strategy Notes (Post-Call)'],
        requirements: 'Must be booked at least 24 hours in advance. We recommend sending any context, links, or messy scratchpads upon booking or at least 12 hours prior to maximize our live time.',
        notes: 'A calendar will be sent to schedule your session upon receipt of payment and all materials.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '03',
      name: 'PRESENTATION POLISH',
      desc: 'Bring us your deck and notes, and we’ll make it undeniably engaging. Pricing varies based on slide volume.',
      modalDesc: 'Bring us your deck and notes, and we’ll make it undeniably engaging. You must provide the rough content/outline and we shape it into a compelling story.\n\nNote: Fidelity of the final delivery is dependent on the state of the content and outline provided. Less complete and coherent materials with little direction may result in a polished “work in progress” deck.',
      time: '1 DAY',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: [
          'Polished Presentation Deck',
          'Copywriting & Narrative Flow Updates',
          'Basic Visual Cleanup & Formatting',
          '30-min Video or Phone Walkthrough (Optional)'
        ],
        requirements: 'At the moment, we can only support Google Slides presentations.\n\nWe recommend sending brand style guidelines, any context, links, or notes upon booking.',
        notes: '24-48 hour turnaround time begins upon receipt of payment and all materials.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '04',
      name: 'CREATIVE REVIEW',
      desc: 'Need an extra pair of creative eyes? We provide a thorough review and actionable feedback grounded in your strategy and brand guidelines. Pricing is based on volume, format and deadlines.',
      modalDesc: 'Need an extra pair of creative eyes? We provide a thorough review and actionable feedback grounded in your strategy and brand guidelines. We can review all formats: video, social content, ads, web copy, emails, reports, event activations, and more. Pricing varies based on asset volume and deadlines.',
      time: '1 DAY',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['Detailed Feedback Document', 'Marked-up Assets (where applicable)', 'Actionable Next-Steps Checklist'],
        requirements: 'Please provide the original creative brief and brand guidelines alongside the assets for the most accurate review.',
        notes: 'Turnaround time is dependent on the number of assets. Work begins upon receipt of payment and materials.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '05',
      name: 'LATE NIGHT RUN',
      desc: 'Emergency overnight strategy and creative direction. This option provides flexible strategic and creative support for an urgent pitch or presentation.',
      modalDesc: 'Emergency overnight strategy and creative direction. This option provides flexible strategic and creative support for an urgent pitch or presentation. Subject to availability. Request must be submitted by 5 PM PT / 8 PM ET and accompanied by a live kick-off call.',
      time: 'ASAP',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['Overnight Execution', 'Dedicated Strategy, Creative, and Design Support', 'Morning Hand-off Call'],
        requirements: 'Subject to availability. Request must be submitted by 5 PM PT / 8 PM ET and accompanied by a live kick-off call.',
        notes: 'This is a more robust service that includes a combination of offerings (brief checkup, strategy chat, presentation polish, and creative review). Final deliverables are dependent on your needs and will be aligned in the kick-off call.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '06',
      name: '1-WEEK SPRINT',
      desc: 'Week-long strategy and creative support. Need to quickly prototype an idea? Build a viable program or product plan? Have an upcoming launch or deadline? This option is for large custom scopes.',
      modalDesc: 'Week-long strategy and creative support. Need to quickly prototype an idea? Build a viable program or product plan? Have an upcoming launch or deadline? This option is for large custom scopes. Request must be submitted by 5 PM PT / 8 PM ET and accompanied by a live kick-off call.',
      time: '1 WEEK',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['Custom Deliverables Based on Needs', 'Dedicated Strategy, Creative, and Design Support', 'Live Daily Updates'],
        requirements: 'Subject to availability. Request must be submitted by 5 PM PT / 8 PM ET and accompanied by a live kick-off call.',
        notes: 'This is a more robust service that includes a combination of offerings (brief checkup, strategy chat, presentation polish, and creative review). Final deliverables are dependent on your needs and will be aligned in the kick-off call.',
        pricing: 'Pricing varies based on needs and deliverables and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    },
    {
      id: '07',
      name: 'AI TRAINING',
      desc: 'Human-centered AI Foundations training for business owners and professional teams tailored to your goals, objectives, and work.',
      modalDesc: 'Interested in getting your team started with AI for work but not sure of where to begin? This AI Foundations training will empower you and your team with fundamental skills to navigate this new era of innovation. The session uniquely centers on a collective-minded approach to AI adoption and covers responsible AI use, prompting essentials, real-life use cases, and how to develop an adaptive, strategic mindset for driving better solutions and innovation in your work.',
      time: '2 WEEKS',
      price: 'PRICE VARIES',
      basePriceNum: 0,
      isCustom: true,
      details: {
        deliverables: ['Hands-on Workshop (In-person or Online)', 'Bespoke AI Use Cases', 'AI Action Plan Handbook'],
        requirements: 'Training participants will need to have access to AI tools, like LLMs such as Google Gemini, Microsoft Copilot, ChatGPT, etc.',
        notes: 'The training session length, format, and content will be custom-tailored to your business and team needs and proficiency level. Additional exercises and sessions can be integrated, such as team building or creative sprints.',
        pricing: 'Pricing varies based on size (# of people) and content scope and will be determined after the kick-off call. Click "Add to Basket" to proceed with scheduling a chat.'
      }
    }
  ];

  const aisles = [
    {
      title: "AISLE 1: POPULAR ITEMS",
      items: menuItems.slice(0, 4)
    },
    {
      title: "AISLE 2: BESPOKE OPTIONS",
      items: menuItems.slice(4)
    }
  ];

  const faqs = [
    {
      q: "What is Konbini Creative Co.?",
      a: <>Konbini Creative Co. is a multi-disciplinary marketing and creative agency inspired by Japan’s iconic and humble convenience stores, or “konbini.” Konbini are essential to everyday life and provide high-quality goods for every need. Likewise, Konbini Creative Co. offers essential, world-class strategy and creative services to help you achieve your business objectives, tackle creative briefs and execution, sprint and prototype ideas, and more. We help clients turn their ambitions into results.</>
    },
    {
      q: "Who is Konbini Creative Co. for?",
      a: <>Konbini Creative Co. offers marketing strategy, creative direction, and execution support for entrepreneurs, in-house marketing teams, agencies, and businesses and organizations of all sizes. If you are interested in working with us, please submit the contact form <a href="#contact-us" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">here</a>.</>
    },
    {
      q: "How does the process work?",
      a: "Simply select your services and add it to your basket. Then complete the form to submit your request and we will email an invoice with a payment link. Next, you will receive an intake form for your materials. Work begins upon receipt of payment and all materials. Need us to sign a NDA? No problem. All your information is kept secure and private."
    },
    {
      q: "Does Konbini Creative Co. offer retainer or custom scopes?",
      a: <>Yes, we certainly can accommodate a retainer engagement or custom scope. Please reach out to us in the <a href="#contact-us" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">contact form</a> and we will get back to you immediately to learn more about your needs.</>
    },
    {
      q: "Is Konbini Creative Co. hiring?",
      a: <>As we grow, we’re keeping an eye out for culturally fluent creatives and hybrid thinkers who lead with curiosity and kindness and have a range of experience. If this is you, send us your resume and portfolio at <a href="mailto:careers@konbinicreative.co" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">careers@konbinicreative.co</a>.</>
    }
  ];

  // --- LEGAL PAGES RENDERING ---
  if (currentPath === '/privacy') {
    return (
      <div className="min-h-screen bg-white text-blue-700 font-sans selection:bg-blue-700 selection:text-white pb-12">
        <nav className="p-4 border-b-2 border-blue-700 sticky top-0 bg-white z-50 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="/" onClick={(e) => navigateTo('/', e)} className="text-xl md:text-2xl font-black tracking-tighter leading-none hover:opacity-70 transition-opacity">
              KONBINI<br/>CREATIVE CO.
            </a>
          </div>
          <a href="/" onClick={(e) => navigateTo('/', e)} className="font-mono text-sm font-bold border-2 border-blue-700 px-4 py-2 hover:bg-blue-700 hover:text-white transition-colors">
            RETURN TO STORE
          </a>
        </nav>
       
        <main className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <div className="mb-12">
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-4 uppercase">
              Privacy Policy
            </h1>
            <div className="font-mono text-sm opacity-70 flex flex-col sm:flex-row sm:gap-6">
              <span>Effective Date: April 1, 2026</span>
              <span>Last Updated: April 1, 2026</span>
            </div>
          </div>
         
          <div className="font-mono text-lg leading-relaxed mb-12">
            <p className="mb-4">This Privacy Policy explains how Konbini Creative Co. ("we," "us," or "our") collects, uses, and discloses information about you when you visit our website, konbinicreative.co (the "Site"), or interact with us.</p>
            <p>By using the Site, you agree to the collection and use of information in accordance with this policy.</p>
          </div>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">1. Information We Collect</h2>
            <p className="font-mono text-lg leading-relaxed mb-6">We collect information from you directly when you provide it to us, as well as automatically when you use our Site.</p>
           
            <div className="space-y-6">
              <div>
                <h3 className="font-black text-xl mb-2">A. Information You Provide to Us</h3>
                <p className="font-mono text-lg leading-relaxed mb-2">When you fill out a contact form or communicate with us directly, we may collect the following personal information:</p>
                <ul className="list-disc list-inside font-mono text-lg leading-relaxed ml-4">
                  <li>First and Last Name</li>
                  <li>Email Address</li>
                  <li>Company Name</li>
                  <li>Any additional information you choose to provide in your message</li>
                </ul>
              </div>

              <div>
                <h3 className="font-black text-xl mb-2">B. Information Collected Automatically (Analytics)</h3>
                <p className="font-mono text-lg leading-relaxed mb-2">When you visit our Site, we automatically collect certain information about your device and how you interact with our website. This data does not contain personally identifiable information (PII) like your name or email, but may include:</p>
                <ul className="list-disc list-inside font-mono text-lg leading-relaxed ml-4 mb-4">
                  <li>Your IP address (used to determine general location and track user behavior)</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited, time spent on pages, and referral URLs</li>
                </ul>
                <p className="font-mono text-lg leading-relaxed">We use third-party analytics tools, such as Google Analytics, to collect and analyze this data to help us improve the Site's functionality and user experience.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">2. How We Use Your Information</h2>
            <p className="font-mono text-lg leading-relaxed mb-2">We use the information we collect for the following business purposes:</p>
            <ul className="list-disc list-inside font-mono text-lg leading-relaxed ml-4">
              <li>To respond to your inquiries, customer service requests, and contact form submissions.</li>
              <li>To process and fulfill your requests and send related information, including invoices and intake forms.</li>
              <li>To analyze website usage and user behavior to improve our Site, services, and user experience.</li>
              <li>To detect, prevent, and address technical issues or fraudulent activity.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">3. How We Share Your Information</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following situations:</p>
            <ul className="space-y-4 font-mono text-lg leading-relaxed ml-4 list-disc list-outside">
              <li><span className="font-bold">Service Providers:</span> We may share data with trusted third-party vendors who assist us in operating our website, processing payments, or analyzing site traffic (e.g., payment gateways, analytics providers, and web hosting services). These parties are obligated to keep your information confidential and secure.</li>
              <li><span className="font-bold">Legal Compliance:</span> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">4. Cookies and Tracking Technologies</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">We use cookies and similar tracking technologies to track activity on our Site and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.</p>
            <p className="font-mono text-lg leading-relaxed">You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of our Site (such as the checkout process) may not function properly.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">5. Data Security</h2>
            <p className="font-mono text-lg leading-relaxed">The security of your data is important to us. We implement standard administrative, technical, and physical security measures—such as Secure Socket Layer (SSL) encryption—to protect your personal and payment information. However, please remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">6. Your Rights Regarding Your Data</h2>
            <p className="font-mono text-lg leading-relaxed mb-2">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside font-mono text-lg leading-relaxed ml-4 mb-4">
              <li>Request access to the personal data we hold about you.</li>
              <li>Request that we correct any inaccurate or incomplete data.</li>
              <li>Request that we delete your personal data.</li>
            </ul>
            <p className="font-mono text-lg leading-relaxed">To exercise any of these rights, please contact us using the information provided below.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">7. Links to Other Websites</h2>
            <p className="font-mono text-lg leading-relaxed">Our Site may contain links to other websites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">8. Changes to This Privacy Policy</h2>
            <p className="font-mono text-lg leading-relaxed">We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. You are advised to review this Privacy Policy periodically for any changes.</p>
          </section>

          <section>
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">9. Contact Us</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">If you have any questions about this Privacy Policy or how we handle your data, please contact us at:</p>
            <p className="font-mono text-lg leading-relaxed">
              Email: <a href="mailto:service@konbinicreative.co" className="font-bold underline decoration-2 underline-offset-4 hover:bg-blue-700 hover:text-white transition-colors">service@konbinicreative.co</a>
            </p>
          </section>
        </main>

        <footer className="max-w-3xl mx-auto px-4 mt-12 pt-12 border-t-2 border-blue-700 text-center flex flex-col items-center">
          <div className="font-mono text-[10px] opacity-70 uppercase text-center">
            © 2026 KONBINI CREATIVE CO.
          </div>
        </footer>
      </div>
    );
  }

  if (currentPath === '/terms') {
    return (
      <div className="min-h-screen bg-white text-blue-700 font-sans selection:bg-blue-700 selection:text-white pb-12">
        <nav className="p-4 border-b-2 border-blue-700 sticky top-0 bg-white z-50 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="/" onClick={(e) => navigateTo('/', e)} className="text-xl md:text-2xl font-black tracking-tighter leading-none hover:opacity-70 transition-opacity">
              KONBINI<br/>CREATIVE CO.
            </a>
          </div>
          <a href="/" onClick={(e) => navigateTo('/', e)} className="font-mono text-sm font-bold border-2 border-blue-700 px-4 py-2 hover:bg-blue-700 hover:text-white transition-colors">
            RETURN TO STORE
          </a>
        </nav>
       
        <main className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <div className="mb-12">
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-4 uppercase">
              Terms of Service
            </h1>
            <div className="font-mono text-sm opacity-70 flex flex-col sm:flex-row sm:gap-6">
              <span>Effective Date: April 1, 2026</span>
              <span>Last Updated: April 1, 2026</span>
            </div>
          </div>
         
          <div className="font-mono text-lg leading-relaxed mb-12">
            <p className="mb-4">Welcome to Konbini Creative Co. These Terms of Service ("Terms") govern your access to and use of konbinicreative.co (the "Site") and the creative marketing and strategic consultation services provided by Konbini Creative Co. ("we," "us," or "our").</p>
            <p>By accessing our Site, booking a consultation, or purchasing our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Site or book our services.</p>
          </div>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">1. Services Provided</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">We provide creative marketing consultation services, which may include strategy sessions, brand guidance, and marketing advice (the "Services"). These Services are booked through our site and paid for through our online invoicing system.</p>
            <p className="font-mono text-lg leading-relaxed">The advice and strategies provided during our consultations are tailored to the information you provide. The success of any marketing strategy depends on numerous factors outside of our control, including your execution, market conditions, and audience behavior.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">2. Payment and Billing</h2>
            <ul className="space-y-4 font-mono text-lg leading-relaxed ml-4 list-disc list-outside">
              <li><span className="font-bold">Upfront Payment:</span> All consultation services require full payment through our online invoicing system. Your appointment is not confirmed until payment is successfully processed.</li>
              <li><span className="font-bold">Payment Processing:</span> We use third-party payment processors (e.g., Stripe) to securely handle transactions. We do not store your full credit card information on our servers.</li>
              <li><span className="font-bold">Pricing:</span> Prices for our Services are subject to change without notice. The price charged will be the price in effect at the time you complete your booking.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">3. Refund and Rescheduling Policy</h2>
            <ul className="space-y-4 font-mono text-lg leading-relaxed ml-4 list-disc list-outside">
              <li><span className="font-bold">All Sales are Final:</span> Due to the nature of creative consulting, you are purchasing our time, strategic insights, and proprietary marketing advice. Once time has been booked and a transaction is completed, all sales are final and non-refundable.</li>
              <li><span className="font-bold">Rescheduling:</span> We understand that emergencies happen. If you need to reschedule your consultation, you must do so at least 24 hours prior to your scheduled appointment time using the link provided in your confirmation email. Failure to attend a scheduled session without prior notice ("no-show") will result in the forfeiture of your payment.</li>
              <li><span className="font-bold">Feedback:</span> We are committed to delivering high-value, actionable strategies. If you have feedback regarding your session, please email us directly at <a href="mailto:service@konbinicreative.co" className="underline decoration-2 underline-offset-4 hover:bg-blue-700 hover:text-white transition-colors">service@konbinicreative.co</a>. We value open communication and are always eager to ensure you get the most out of our partnership.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">4. Intellectual Property</h2>
            <ul className="space-y-4 font-mono text-lg leading-relaxed ml-4 list-disc list-outside">
              <li><span className="font-bold">Our Intellectual Property:</span> All content on this Site, including text, graphics, logos, and the underlying methodologies, frameworks, or templates used during our consultations, are the exclusive property of Konbini Creative Co. and are protected by copyright and other intellectual property laws.</li>
              <li><span className="font-bold">License to Use:</span> Upon delivery of our consulting Services, we grant you a limited, non-exclusive, non-transferable license to use the ideas, strategies, and notes generated during our sessions strictly for your own internal business purposes. You may not resell or publicly redistribute our proprietary frameworks.</li>
              <li><span className="font-bold">Your Intellectual Property:</span> You retain all ownership rights to your own business data, logos, and pre-existing materials that you share with us during the consultation.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">5. User Responsibilities</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">To ensure the best possible outcome from our Services, you agree to:</p>
            <ul className="list-disc list-inside font-mono text-lg leading-relaxed ml-4 space-y-2">
              <li>Provide accurate, current, and complete information during the booking and onboarding process.</li>
              <li>Actively participate and collaborate during the consultation session.</li>
              <li>Understand that we provide guidance and strategy, but you are solely responsible for the implementation and execution of any marketing campaigns.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">6. Disclaimer of Warranties</h2>
            <p className="font-mono text-lg leading-relaxed">Our Services are provided on an "as is" and "as available" basis. While we strive to provide excellent, industry-standard creative marketing advice, we make no guarantees, representations, or warranties regarding specific financial results, return on investment (ROI), increased sales, or business growth resulting from the use of our Services.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">7. Limitation of Liability</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">To the fullest extent permitted by applicable law, Konbini Creative Co., its owners, employees, or contractors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Services; (ii) any conduct or content of any third party on the Services; or (iii) any outcomes resulting from the implementation of our advice.</p>
            <p className="font-mono text-lg leading-relaxed">In no event shall our total liability to you for all claims arising out of or relating to these Terms or your use of the Services exceed the amount you actually paid to us for the specific consultation in dispute.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">8. Governing Law and Dispute Resolution</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">These Terms shall be governed and construed in accordance with the laws of the State of Hawaii, United States, without regard to its conflict of law provisions.</p>
            <p className="font-mono text-lg leading-relaxed">Any dispute arising out of or relating to these Terms or the breach thereof shall be resolved through good-faith negotiation. If a resolution cannot be reached, the dispute shall be subject to the exclusive jurisdiction of the state and federal courts located in Honolulu, Hawaii.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">9. Changes to These Terms</h2>
            <p className="font-mono text-lg leading-relaxed">We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will indicate the date of the most recent changes at the top of this page. By continuing to access or use our Site or Services after those revisions become effective, you agree to be bound by the revised terms.</p>
          </section>

          <section>
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">10. Contact Us</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">If you have any questions about these Terms, please contact us at:</p>
            <p className="font-mono text-lg leading-relaxed">
              Email: <a href="mailto:service@konbinicreative.co" className="font-bold underline decoration-2 underline-offset-4 hover:bg-blue-700 hover:text-white transition-colors">service@konbinicreative.co</a>
            </p>
          </section>
        </main>

        <footer className="max-w-3xl mx-auto px-4 mt-12 pt-12 border-t-2 border-blue-700 text-center flex flex-col items-center">
          <div className="font-mono text-[10px] opacity-70 uppercase text-center">
            © 2026 KONBINI CREATIVE CO.
          </div>
        </footer>
      </div>
    );
  }

  if (currentPath === '/refund') {
    return (
      <div className="min-h-screen bg-white text-blue-700 font-sans selection:bg-blue-700 selection:text-white pb-12">
        <nav className="p-4 border-b-2 border-blue-700 sticky top-0 bg-white z-50 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href="/" onClick={(e) => navigateTo('/', e)} className="text-xl md:text-2xl font-black tracking-tighter leading-none hover:opacity-70 transition-opacity">
              KONBINI<br/>CREATIVE CO.
            </a>
          </div>
          <a href="/" onClick={(e) => navigateTo('/', e)} className="font-mono text-sm font-bold border-2 border-blue-700 px-4 py-2 hover:bg-blue-700 hover:text-white transition-colors">
            RETURN TO STORE
          </a>
        </nav>
       
        <main className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-12 uppercase">
            Refund Policy
          </h1>
         
          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">All Sales Are Final</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">
              Due to the custom, creative, and intellectual nature of our services and deliverables, all sales are considered final and non-refundable.
            </p>
            <p className="font-mono text-lg leading-relaxed">
              We dedicate significant time, care, and resources to producing high-quality work tailored to your specific needs. As such, we do not offer refunds, returns, or exchanges at this time. By engaging our services, you acknowledge and agree to this policy.
            </p>
          </section>
         
          <section className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">Our Commitment to Your Satisfaction</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">
              Your satisfaction and overall experience with Konbini Creative Co. are important to us. We stand firmly behind the value of our work.
            </p>
            <p className="font-mono text-lg leading-relaxed">
              If you have concerns about a deliverable, believe there is an error or room for correction, or wish to provide feedback on your experience, please contact us immediately.
            </p>
          </section>
         
          <section>
            <h2 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">How to Contact Us</h2>
            <p className="font-mono text-lg leading-relaxed mb-4">
              To submit feedback or discuss a concern, please email us at:
            </p>
            <a href="mailto:service@konbinicreative.co" className="font-mono text-xl font-bold underline decoration-2 underline-offset-4 hover:bg-blue-700 hover:text-white transition-colors">
              service@konbinicreative.co
            </a>
            <p className="font-mono text-lg leading-relaxed mt-6">
              We personally review all correspondence and use your valuable input to continually refine and improve our services and future offerings.
            </p>
          </section>
        </main>

        <footer className="max-w-3xl mx-auto px-4 mt-12 pt-12 border-t-2 border-blue-700 text-center flex flex-col items-center">
          <div className="font-mono text-[10px] opacity-70 uppercase text-center">
            © 2026 KONBINI CREATIVE CO.
          </div>
        </footer>
      </div>
    );
  }
  // --- END LEGAL PAGES ---

  return (
    <div className="min-h-screen bg-white text-blue-700 font-sans selection:bg-blue-700 selection:text-white pb-12">
      {/* Custom Styles for Marquee and specific utilities */}
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: 200%;
          animation: marquee 15s linear infinite;
        }
        .dotted-border {
          border-image: repeating-linear-gradient(to right, #1d4ed8 0, #1d4ed8 4px, transparent 4px, transparent 8px) 1;
          border-bottom-width: 2px;
        }
      `}</style>

      {/* Ticker Tape */}
      <div className="border-b-2 border-blue-700 overflow-hidden bg-white py-1">
        <div className="animate-marquee whitespace-nowrap font-mono text-xs uppercase tracking-widest font-bold">
          <span>
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
            KONBINI CREATIVE CO. // ONE-STOP SHOP // FRESH IDEAS DAILY //
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex justify-between items-center p-4 border-b-2 border-blue-700 sticky top-0 bg-white z-50">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl md:text-3xl font-black tracking-tighter leading-none">
            KONBINI<br/>CREATIVE CO.
          </h1>
          <span className="hidden md:block font-mono text-xs border border-blue-700 px-2 py-1 rounded-sm">
            コンビニ
          </span>
        </div>
       
        <div className="flex items-center gap-6 font-mono text-sm">
          <div className="hidden md:flex items-center gap-2">
            <Clock size={16} />
            <span>{time}</span>
          </div>
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex items-center gap-2 hover:bg-blue-700 hover:text-white px-3 py-1 transition-colors border-2 border-transparent hover:border-blue-700 rounded-full"
          >
            <ShoppingBag size={18} />
            <span className="font-bold">BASKET ({cart.length})</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="px-4 py-16 md:py-24 border-b-2 border-blue-700">
        <div className="max-w-4xl uppercase">
          <h2 className="text-[12vw] md:text-8xl font-black tracking-tighter leading-[0.9] mb-6">
            Meet your<br/>neighborhood<br/>creative shop.
          </h2>
          <p className="font-mono text-lg md:text-xl max-w-2xl leading-relaxed">
            Konbini Creative Co. is a multi-disciplinary marketing agency. A one-stop shop for brand strategy, creative innovation, people-centered storytelling, and business transformation.
          </p>
        </div>
      </header>

{/* About Us Section */}
      <section className="max-w-6xl mx-auto px-4 py-12 border-b-2 border-blue-700">
        <div className="flex justify-between items-end mb-4 border-b-4 border-blue-700 pb-2">
          <h3 className="text-3xl sm:text-3xl font-black tracking-tight uppercase break-words pr-4">ABOUT US</h3>
          <span className="font-mono text-sm font-bold hidden md:block">OUR STORY</span>
        </div>

        <div className="flex flex-col">
          <div className="py-6">
            <div className="font-mono text-sm md:text-base leading-relaxed max-w-4xl pr-8 space-y-4">
              <p>Konbini Creative Co. was founded by Zeny Picone (<a href="https://linkedin.com/in/zenypicone" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">LinkedIn</a>, <a href="https://zenypicone.com" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">Portfolio</a>), a former brand lead, creative strategist, and product marketer at Google, who sought a new model for collaboration to meet companies' need for deep, fast, and bespoke expertise. After making countless “konbini” (convenience store) stops in Tokyo with her kids, Zeny realized how important variety, accessibility, and speed were and that convenience does not mean sacrificing quality or cutting corners——in fact, konbini were a breeding ground for innovation and people-first principles.</p>
              <p>Konbini Creative Co.’s mission is to serve ‘fresh ideas daily’ to clients located around the corner and around the world. Headquartered in Honolulu, Hawai‘i——every Konbini Creative Co. project is supported by skilled writers, designers, creatives, and technologists with Hawaiʻi roots.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Store Info / Process */}
      <section className="max-w-6xl mx-auto px-4 py-12 border-b-2 border-blue-700">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y-2 md:divide-y-0 md:divide-x-2 divide-blue-700">
          <div className="p-8 md:p-12 flex flex-col items-center text-center">
            <span className="font-mono text-4xl font-bold mb-4">01.</span>
            <h4 className="text-xl font-black uppercase mb-2">Pick Your Goods</h4>
            <p className="font-mono text-sm">Select your services from our shelves. Don't see exactly what you need? <br/><a href="#contact-us" className="underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">Contact us.</a></p>
          </div>
          <div className="p-8 md:p-12 flex flex-col items-center text-center">
            <span className="font-mono text-4xl font-bold mb-4">02.</span>
            <h4 className="text-xl font-black uppercase mb-2">Checkout</h4>
            <p className="font-mono text-sm">Submit your basket and we’ll send you an invoice and intake form for your materials. We get to work instantly.</p>
          </div>
          <div className="p-8 md:p-12 flex flex-col items-center text-center">
            <span className="font-mono text-4xl font-bold mb-4">03.</span>
            <h4 className="text-xl font-black uppercase mb-2">Consume Fresh</h4>
            <p className="font-mono text-sm">Receive specific and actionable strategic feedback and direction within the allotted time.</p>
          </div>
        </div>
      </section>

      {/* Main Menu Grid */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {aisles.map((aisle, index) => (
          <div key={index} className={index > 0 ? "mt-16" : ""}>
            <div className="flex items-end mb-8 border-b-4 border-blue-700 pb-2">
              <h3 className="text-3xl font-black tracking-tight uppercase">{aisle.title}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
              {aisle.items.map((item) => (
                <div key={item.id} className="group relative flex flex-col py-6 dotted-border">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-mono text-xs font-bold mb-1 opacity-70">ITEM NO. {item.id}</div>
                      <h4 className="text-2xl font-black tracking-tight uppercase group-hover:underline decoration-4 underline-offset-4">
                        {item.name}
                      </h4>
                      <div className="font-mono text-sm mb-3 mt-1">{item.jp}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xs opacity-70 border border-blue-700 px-1 inline-block">
                        {item.time}
                      </div>
                    </div>
                  </div>
                 
                  <p className="font-mono text-sm leading-relaxed mb-6 pr-8">
                    {item.desc}
                  </p>

                  <div className="mt-auto">
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="w-full flex justify-between items-center border-2 border-blue-700 py-3 px-4 font-mono font-bold hover:bg-blue-700 hover:text-white transition-all active:scale-[0.98]"
                    >
                      <span>LEARN MORE</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </main>

      {/* FAQs Section */}
      <section className="max-w-6xl mx-auto px-4 py-8 mb-12">
        <div className="flex justify-between items-end mb-4 border-b-4 border-blue-700 pb-2">
          <h3 className="text-3xl font-black tracking-tight uppercase">FREQUENTLY ASKED QUESTIONS</h3>
          <span className="font-mono text-sm font-bold hidden md:block">HELP DESK</span>
        </div>

        <div className="flex flex-col">
          {faqs.map((faq, index) => (
            <div key={index} className="dotted-border">
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="w-full flex justify-between items-center py-6 text-left group"
              >
                <span className="font-black text-xl md:text-2xl uppercase group-hover:underline decoration-4 underline-offset-4 text-left">
                  {faq.q}
                </span>
                <span className="ml-4 flex-shrink-0 border-2 border-transparent group-hover:border-blue-700 rounded-full p-1 transition-colors">
                  {openFaq === index ? <Minus size={24} /> : <Plus size={24} />}
                </span>
              </button>
              {openFaq === index && (
                <div className="pb-8 font-mono text-sm md:text-base leading-relaxed max-w-4xl pr-8">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-us" className="max-w-6xl mx-auto px-4 py-8 mb-12 scroll-mt-24">
        <div className="flex justify-between items-end mb-8 border-b-4 border-blue-700 pb-2">
          <h3 className="text-3xl font-black tracking-tight uppercase">CONTACT US</h3>
          <span className="font-mono text-sm font-bold hidden md:block">INQUIRIES</span>
        </div>

        <div className="border-2 border-blue-700 p-6 md:p-10 relative">
          {isContactSubmitted ? (
            <div className="py-16 flex flex-col items-center text-center animate-in fade-in duration-500">
              <CheckCircle2 size={48} className="text-blue-700 mb-6" />
              <h4 className="text-3xl font-black uppercase mb-4">Message Received</h4>
              <p className="font-mono text-lg max-w-md">Thanks for reaching out! We'll get back to you shortly.</p>
              <button
                onClick={() => setIsContactSubmitted(false)}
                className="mt-10 border-2 border-blue-700 px-6 py-2 font-mono font-bold hover:bg-blue-700 hover:text-white transition-colors uppercase text-sm"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col">
                  <label className="font-mono text-sm font-bold mb-2 uppercase">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="border-2 border-blue-700 p-3 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-mono text-sm font-bold mb-2 uppercase">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="border-2 border-blue-700 p-3 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-mono text-sm font-bold mb-2 uppercase">Title</label>
                  <input
                    type="text"
                    name="title"
                    className="border-2 border-blue-700 p-3 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-mono text-sm font-bold mb-2 uppercase">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="border-2 border-blue-700 p-3 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors"
                  />
                </div>
              </div>
             
              <div className="flex flex-col">
                <label className="font-mono text-sm font-bold mb-2 uppercase">Message</label>
                <textarea
                  name="message"
                  rows="5"
                  required
                  placeholder="Interested in working with us? Don't find what you need? Reach out and we'll get back to you."
                  className="border-2 border-blue-700 p-4 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors resize-y placeholder:text-blue-700/50"
                ></textarea>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto flex justify-between items-center gap-8 bg-blue-700 text-white py-4 px-8 font-mono font-bold text-xl hover:bg-white hover:text-blue-700 border-2 border-blue-700 transition-all active:scale-[0.98]"
                >
                  <span>SEND MESSAGE</span>
                  <ArrowRight size={24} />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer / Receipt end */}
      <footer className="max-w-3xl mx-auto px-4 mt-8 text-center flex flex-col items-center">
        <h2 className="text-4xl font-black tracking-tighter mb-6">THANK YOU<br/>COME AGAIN</h2>
       
        <div className="flex gap-6 mb-16 font-mono text-sm font-bold flex-wrap justify-center">
          <div className="flex items-center gap-1">FRESH IDEAS SERVED DAILY</div>
        </div>
       
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6 font-mono text-xs uppercase font-bold opacity-80">
          <a href="/privacy" onClick={(e) => navigateTo('/privacy', e)} className="hover:opacity-100 hover:underline decoration-2 underline-offset-4 transition-all">Privacy Policy</a>
          <a href="/terms" onClick={(e) => navigateTo('/terms', e)} className="hover:opacity-100 hover:underline decoration-2 underline-offset-4 transition-all">Terms of Service</a>
          <a href="/refund" onClick={(e) => navigateTo('/refund', e)} className="hover:opacity-100 hover:underline decoration-2 underline-offset-4 transition-all">Refund Policy</a>
        </div>
       
        <div className="font-mono text-[10px] opacity-70 uppercase text-center">
          © 2026 KONBINI CREATIVE CO.
        </div>
      </footer>

      {/* Product Details Modal / Overlay */}
      {selectedItem && (
        <div className="fixed inset-0 z-[100] bg-white overflow-y-auto selection:bg-blue-700 selection:text-white">
          <div className="border-b-2 border-blue-700 sticky top-0 bg-white p-4 flex justify-between items-center z-10">
            <button
              onClick={() => setSelectedItem(null)}
              className="flex items-center gap-2 font-bold font-mono hover:bg-blue-700 hover:text-white px-3 py-2 border-2 border-transparent hover:border-blue-700 transition-colors"
            >
              <ArrowLeft size={20} /> BACK TO MENU
            </button>
            <button
              onClick={() => setSelectedItem(null)}
              className="p-1 hover:bg-blue-700 hover:text-white border-2 border-transparent hover:border-blue-700 transition-colors"
            >
              <X size={28} />
            </button>
          </div>
         
          <div className="max-w-5xl mx-auto px-4 py-12 md:py-20 text-blue-700">
            {/* Header */}
            <div className="mb-12 border-b-4 border-blue-700 pb-8">
              <div className="font-mono text-lg font-bold mb-2 opacity-70">ITEM NO. {selectedItem.id}</div>
              <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tight mb-2">{selectedItem.name}</h2>
              <div className="font-mono text-xl mb-6">{selectedItem.jp}</div>
              <div className="flex flex-wrap gap-4 font-mono">
                <span className="border-2 border-blue-700 px-3 py-1 font-bold bg-blue-700 text-white">{selectedItem.price}</span>
                <span className="border-2 border-blue-700 px-3 py-1 font-bold">{selectedItem.time}</span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 font-mono">
              <div>
                <h4 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">Description</h4>
                <p className="mb-10 text-lg leading-relaxed whitespace-pre-wrap">{selectedItem.modalDesc || selectedItem.desc}</p>
               
                <h4 className="text-2xl font-black uppercase mb-4 border-b-2 border-blue-700 pb-2 inline-block">Deliverables</h4>
                <ul className="list-none space-y-3 mb-8">
                  {selectedItem.details.deliverables.map((d, i) => (
                    <li key={i} className="flex gap-3 items-start text-base">
                      <CheckCircle2 size={20} className="flex-shrink-0 mt-0.5" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
             
              <div className="space-y-8">
                <div className="p-6 border-2 border-blue-700 bg-blue-50/50">
                  <h4 className="text-xl font-black uppercase mb-3 flex items-center gap-2">
                    Pricing Structure
                  </h4>
                  <p className="leading-relaxed">{selectedItem.details.pricing}</p>
                </div>
               
                <div className="p-6 border-2 border-blue-700">
                  <h4 className="text-xl font-black uppercase mb-3 text-blue-700 border-b border-blue-700/30 pb-2">
                    Requirements
                  </h4>
                  <p className="leading-relaxed mb-6 whitespace-pre-wrap">{selectedItem.details.requirements}</p>
                 
                  <h4 className="text-xl font-black uppercase mb-3 border-b border-blue-700/30 pb-2">
                    Notes
                  </h4>
                  <p className="leading-relaxed whitespace-pre-wrap">{selectedItem.details.notes}</p>
                </div>
              </div>
            </div>

            {/* Action Bar */}
            <div className="mt-16 pt-8 border-t-2 border-blue-700 flex flex-col sm:flex-row gap-4 justify-between items-center">
              <span className="font-mono text-sm opacity-70">Ready to proceed? Add to your basket and click submit request.</span>
              <button
                onClick={() => {
                  addToCart(selectedItem);
                  setSelectedItem(null);
                }}
                className="w-full sm:w-auto flex justify-between items-center gap-8 bg-blue-700 text-white py-4 px-8 font-mono font-bold text-xl hover:bg-white hover:text-blue-700 border-2 border-blue-700 transition-all active:scale-[0.98]"
              >
                <span>ADD TO BASKET</span>
                <ArrowRight size={24} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Slide-out Overlay */}
      {isCartOpen && (
        <div
          className="fixed inset-0 bg-blue-900/20 z-[105] backdrop-blur-sm"
          onClick={() => { setIsCartOpen(false); setCartSubmitSuccess(false); }} 
        />
      )}

      {/* Cart Slide-out Panel */}
      <div className={`fixed inset-y-0 right-0 w-full md:w-[450px] bg-white border-l-2 border-blue-700 z-[110] transform transition-transform duration-300 ease-in-out flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6 border-b-2 border-blue-700 flex justify-between items-center bg-white shrink-0">
          <h2 className="text-3xl font-black uppercase tracking-tight">Your Basket</h2>
          <button
            onClick={() => { setIsCartOpen(false); setCartSubmitSuccess(false); }}
            className="p-1 hover:bg-blue-700 hover:text-white border-2 border-transparent hover:border-blue-700 transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-blue-50/30">
          {cart.length === 0 ? (
            <div className="text-center font-mono opacity-50 py-12">
              Your basket is empty.<br/>Grab some fresh ideas!
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="border-2 border-blue-700 bg-white p-4 relative flex flex-col">
                <div className="flex justify-between items-start mb-2 pr-8">
                  <div>
                    <div className="font-mono text-xs font-bold opacity-70 mb-1">ITEM NO. {item.id}</div>
                    <h4 className="font-black uppercase text-lg leading-tight">{item.name}</h4>
                  </div>
                  <div className="font-mono font-bold text-lg">
                    {item.isCustom ? 'CUSTOM' : `$${calculateItemTotal(item)}`}
                  </div>
                </div>

                <button
                  onClick={() => removeFromCart(item.id)}
                  className="absolute top-4 right-4 text-blue-700/50 hover:text-blue-700 transition-colors"
                  aria-label="Remove item"
                >
                  <Trash2 size={20} />
                </button>

                {/* Variable Pricing Input */}
                {item.variableConfig && (
                  <div className="mt-4 pt-4 border-t border-blue-700/20 flex justify-between items-center">
                    <label className="font-mono text-sm uppercase font-bold">{item.variableConfig.label}</label>
                    <input
                      type="number"
                      min={item.variableConfig.min}
                      value={item.variableCount}
                      onChange={(e) => updateVariableCount(item.id, e.target.value)}
                      className="border-2 border-blue-700 w-20 p-1 text-center font-mono font-bold focus:outline-none focus:bg-blue-50"
                    />
                  </div>
                )}

                {/* Quantity Controls */}
                <div className="mt-4 pt-4 border-t border-blue-700/20 flex justify-between items-center">
                  <span className="font-mono text-sm uppercase font-bold">Quantity</span>
                  <div className="flex items-center border-2 border-blue-700 font-mono font-bold">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="px-3 py-1 hover:bg-blue-700 hover:text-white transition-colors border-r-2 border-blue-700"
                    >
                      -
                    </button>
                    <span className="px-4 py-1">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="px-3 py-1 hover:bg-blue-700 hover:text-white transition-colors border-l-2 border-blue-700"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-6 border-t-2 border-blue-700 bg-white shrink-0">
          {cartSubmitSuccess ? (
            <div className="py-8 flex flex-col items-center text-center animate-in fade-in duration-500">
              <CheckCircle2 size={48} className="text-blue-700 mb-4" />
              <h4 className="text-2xl font-black uppercase mb-2">Request Submitted</h4>
              <p className="font-mono text-sm max-w-[250px] mb-6">We've received your basket and will be in touch shortly to finalize details and payment.</p>
              <button 
                onClick={() => { setIsCartOpen(false); setCartSubmitSuccess(false); }}
                className="w-full bg-blue-700 text-white py-3 font-mono font-bold hover:bg-white hover:text-blue-700 border-2 border-blue-700 transition-colors"
              >
                CLOSE BASKET
              </button>
            </div>
          ) : (
            <form onSubmit={handleCartSubmit}>
              <div className="flex flex-col gap-1 mb-4">
                <div className="flex justify-between font-black text-xl uppercase">
              <span>Subtotal</span>
              <span>${cartSubtotal}{hasCustomItems ? '+' : ''}</span>
            </div>
            {hasCustomItems && (
                  <span className="font-mono text-[10px] opacity-70 text-right leading-tight">
                *Plus variable custom pricing for select items.
              </span>
            )}
          </div>

              <div className="flex flex-col gap-3 mb-4">
                <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
                    value={cartForm.name}
                    onChange={(e) => setCartForm({...cartForm, name: e.target.value})}
                    required
                    aria-label="Full Name"
                    placeholder="Full Name *"
                    className="w-full border-2 border-blue-700 p-2 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors placeholder:text-blue-700/60" 
                  />
                  <input 
                    type="email" 
                    value={cartForm.email}
                    onChange={(e) => setCartForm({...cartForm, email: e.target.value})}
                    required
                    aria-label="Email Address"
                    placeholder="Email Address *"
                    className="w-full border-2 border-blue-700 p-2 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors placeholder:text-blue-700/60" 
                  />
                </div>
                <input 
                  type="text" 
                  value={cartForm.company}
                  onChange={(e) => setCartForm({...cartForm, company: e.target.value})}
                  required
                  aria-label="Company or Organization Name"
                  placeholder="Company or Organization Name *"
                  className="w-full border-2 border-blue-700 p-2 font-mono text-sm focus:outline-none focus:bg-blue-50 transition-colors placeholder:text-blue-700/60" 
            />
          </div>

              <div className="mb-5 flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
              className="mt-1 w-5 h-5 border-2 border-blue-700 accent-blue-700 flex-shrink-0 cursor-pointer"
            />
            <label htmlFor="terms" className="font-mono text-xs leading-tight cursor-pointer opacity-80 hover:opacity-100 transition-opacity">
                  I have read and agree to the <a href="/refund" onClick={(e) => { e.stopPropagation(); navigateTo('/refund', e); }} className="font-bold underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">Refund Policy</a> and <a href="/terms" onClick={(e) => { e.stopPropagation(); navigateTo('/terms', e); }} className="font-bold underline decoration-2 underline-offset-2 hover:bg-blue-700 hover:text-white transition-colors">Terms of Service</a>.
            </label>
          </div>

          <button
                type="submit"
                disabled={cart.length === 0 || !termsAccepted || isCartSubmitting}
            className="w-full flex justify-between items-center gap-8 bg-blue-700 text-white py-4 px-8 font-mono font-bold text-xl hover:bg-white hover:text-blue-700 border-2 border-blue-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
          >
                <span>{isCartSubmitting ? 'SUBMITTING...' : 'SUBMIT REQUEST'}</span>
            <ArrowRight size={24} />
          </button>
              <p className="mt-3 text-center font-mono text-[10px] opacity-70 uppercase tracking-wide leading-tight">
                This is not a payment form. You will be sent an invoice after your request is received.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
